/**
 * Validation utilities for metadata standards compliance
 * Based on Magnus Core Metadata Standards v2.0.0
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { ProjectMetadata, FmeaItem } from '../types';

// Constants
export const CURRENT_SCHEMA_VERSION = '2.0.0';
export const SUPPORTED_SCHEMA_VERSIONS = ['1.0.0', '2.0.0'];

/**
 * Sanitize filename according to metadata standards
 */
export function sanitizeFilename(name: string): string {
    return name
        .replace(/[<>:"/\\|?*]/g, '_')  // Remove special characters
        .replace(/\s+/g, '_')            // Replace spaces with underscores
        .substring(0, 255);               // Max length
}

/**
 * Normalize project name for comparison (case-insensitive, ignores underscores vs spaces)
 */
export function normalizeProjectName(name: string): string {
    if (!name) return '';
    return name.toLowerCase().replace(/_/g, ' ').trim();
}

/**
 * Extract project name from an epoch name string based on standard delimiters
 */
export function extractProjectName(epochName: string): string {
    if (!epochName) return 'Other';

    // Primary: "Project - Epoch X" format
    if (epochName.includes(' - ')) {
        return epochName.split(' - ')[0].trim();
    }

    // Special Case: Magnus Genesis naming
    if (epochName.startsWith('Magnus Genesis')) {
        return 'Magnus Genesis';
    }

    // Fallback: Use full name
    return epochName.trim();
}

/**
 * Validate ProjectMetadata for required fields and consistency
 */
export function validateProjectMetadata(metadata: Partial<ProjectMetadata>): {
    valid: boolean;
    errors: string[];
} {
    const errors: string[] = [];

    // Required fields
    if (!metadata.id || metadata.id.trim() === '') {
        errors.push('Project ID is required');
    }

    if (!metadata.name || metadata.name.trim() === '') {
        errors.push('Project name is required');
    }

    if (!metadata.description || metadata.description.trim() === '') {
        errors.push('Project description is required');
    }

    if (!metadata.created) {
        errors.push('Created timestamp is required');
    }

    if (!metadata.lastModified) {
        errors.push('Last modified timestamp is required');
    }

    if (!metadata.version) {
        errors.push('Version is required');
    }

    if (!metadata.author || metadata.author.trim() === '') {
        errors.push('Author is required');
    }

    // Validate schema version if present
    if (metadata.schemaVersion && !SUPPORTED_SCHEMA_VERSIONS.includes(metadata.schemaVersion)) {
        errors.push(`Unsupported schema version: ${metadata.schemaVersion}`);
    }

    return {
        valid: errors.length === 0,
        errors,
    };
}

/**
 * Validate epoch name matches project name
 */
export function validateEpochName(epochName: string, projectName: string): boolean {
    if (!epochName || !projectName) return false;
    return epochName.includes(projectName) || epochName.startsWith(projectName);
}

/**
 * Validate FMEA item has required metadata
 */
export function validateFmeaItem(item: Partial<FmeaItem>): {
    valid: boolean;
    errors: string[];
} {
    const errors: string[] = [];

    if (!item.id || item.id.trim() === '') {
        errors.push('FMEA item ID is required');
    }

    // Warn if missing epochId (not required for legacy items)
    if (item.epochId === undefined) {
        console.warn(`⚠️ FMEA item ${item.id} missing epochId`);
    }

    // Warn if missing projectName (not required for legacy items)
    if (!item.projectName || item.projectName.trim() === '') {
        console.warn(`⚠️ FMEA item ${item.id} missing projectName`);
    }

    return {
        valid: errors.length === 0,
        errors,
    };
}

/**
 * Create default ProjectMetadata
 */
export function createDefaultProjectMetadata(name: string, author: string = 'system'): ProjectMetadata {
    const now = new Date().toISOString();

    return {
        id: crypto.randomUUID(),
        name: sanitizeFilename(name),
        displayName: name,
        description: '',
        created: now,
        lastModified: now,
        version: '1.0.0',
        schemaVersion: CURRENT_SCHEMA_VERSION,
        author,
        createdBy: author,
        modifiedBy: author,
        customer: 'Unassigned',  // Default customer
        projectType: 'Production',
        developmentPhase: 'Concept',
        stage: 'design',  // Default stage
        isExperimental: false,
        status: 'active',  // Default status
        fmeaStandard: 'AIAG_VDA', // Default to VDA 2019
        tags: [],
        changeLog: [{
            timestamp: now,
            user: author,
            changeType: 'created',
            description: 'Project created',
        }],
    };
}

/**
 * Migrate/update existing project metadata to include new fields
 */
const migrateCoreFields = (metadata: Partial<ProjectMetadata>, now: string) => ({
    id: metadata.id || crypto.randomUUID(),
    name: metadata.name || 'Untitled Project',
    displayName: metadata.displayName || metadata.name || 'Untitled Project',
    description: metadata.description || '',
    created: metadata.created || now,
    lastModified: now,
    version: metadata.version || '1.0.0',
    schemaVersion: metadata.schemaVersion || CURRENT_SCHEMA_VERSION,
    author: metadata.author || 'system',
});

const migrateOptionalFields = (metadata: Partial<ProjectMetadata>) => ({
    createdBy: metadata.createdBy || metadata.author || 'system',
    modifiedBy: metadata.modifiedBy || metadata.author || 'system',
    customerId: metadata.customerId,
    productLineId: metadata.productLineId,
    productId: metadata.productId,
    projectType: metadata.projectType || 'Production',
    developmentPhase: metadata.developmentPhase || 'Concept',
    isExperimental: metadata.isExperimental || false,
    targetCertifications: metadata.targetCertifications,
    marketSegment: metadata.marketSegment,
    tags: metadata.tags || [],
    notes: metadata.notes,
    changeLog: metadata.changeLog || [],
});

const migrateNewFields = (metadata: Partial<ProjectMetadata>) => ({
    customer: metadata.customer || 'Unassigned',
    status: metadata.status || 'active',
    stage: metadata.stage || 'design',
    fmeaStandard: metadata.fmeaStandard || 'AIAG_VDA',
});

/**
 * Migrate/update existing project metadata to include new fields
 */
export function migrateProjectMetadata(metadata: Partial<ProjectMetadata>): ProjectMetadata {
    const now = new Date().toISOString();
    return {
        ...migrateCoreFields(metadata, now),
        ...migrateOptionalFields(metadata),
        ...migrateNewFields(metadata),
    } as ProjectMetadata;
}

/**
 * V validate data structure before saving
 */
export function validateBeforeSave(data: any): {
    valid: boolean;
    errors: string[];
    warnings: string[];
} {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for project metadata
    if (!data.projectMetadata) {
        errors.push('Missing projectMetadata');
    } else {
        const metadataValidation = validateProjectMetadata(data.projectMetadata);
        if (!metadataValidation.valid) {
            errors.push(...metadataValidation.errors);
        }
    }

    // Validate epochs reference correct project name
    if (data.iterationHistory && data.projectMetadata) {
        data.iterationHistory.forEach((epoch: any, index: number) => {
            if (epoch.epochName && data.projectMetadata.name) {
                if (!validateEpochName(epoch.epochName, data.projectMetadata.name)) {
                    warnings.push(`Epoch ${index + 1} (ID ${epoch.epochNumber}) name doesn't match project name`);
                }
            }
        });
    }

    // Validate FMEA items
    if (data.fmeaItems) {
        data.fmeaItems.forEach((item: FmeaItem) => {
            const itemValidation = validateFmeaItem(item);
            if (!itemValidation.valid) {
                warnings.push(...itemValidation.errors);
            }
        });
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}
